import ldGroupBy from "lodash-es/groupBy";
import { Injectable, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { LgHelpTooltipServiceGateway } from "./gateway/lg-help-tooltip-gateway";
import { LG_HELP_TOOLTIP_CONFIG, LgHelpTooltipItem } from "./types/types";
import { LG_LOCALIZATION_SETTINGS } from "@logex/framework/lg-localization";

const THROTTLE_INTERVAL = 50;

interface RequestedTooltip {
    key: string;
    lang: string;
    langOverlay?: string | undefined;
}

@Injectable({ providedIn: "root" })
export class LgHelpTooltipService {
    private _gateway = inject(LgHelpTooltipServiceGateway);
    private _localizationSettings = inject(LG_LOCALIZATION_SETTINGS);
    private _cache: Record<string, Promise<LgHelpTooltipItem | null>> = {};
    private _requestedTooltips: RequestedTooltip[] = [];
    private _throttleHandler: any;
    private _responseHandlers: Record<string, (value: LgHelpTooltipItem | null) => void> = {};
    private _lgHelpTooltipConfig = inject(LG_HELP_TOOLTIP_CONFIG);

    async getValue(key: string, lang?: string): Promise<LgHelpTooltipItem | null> {
        if (this._cache[key] != null) {
            return this._cache[key];
        }

        this._cache[key] = new Promise<LgHelpTooltipItem | null>(resolve => {
            this._responseHandlers[key] = resolve;
        });

        // If lang is undefined, use this._localizationSettings.locale
        const definedLang: string = lang || this._localizationSettings.locale;
        this._requestedTooltips.push({
            key,
            lang: definedLang,
            langOverlay: this._lgHelpTooltipConfig.languageOverlay
        });

        clearTimeout(this._throttleHandler);
        this._throttleHandler = setTimeout(async () => {
            const requests = this._requestedTooltips;
            const handlers = this._responseHandlers;
            this._requestedTooltips = [];
            this._responseHandlers = {};
            await this._loadTooltips(requests, handlers);
        }, THROTTLE_INTERVAL);

        return this._cache[key];
    }

    private async _loadTooltips(
        requestedTooltips: RequestedTooltip[],
        responseHandlers: Record<string, (value: LgHelpTooltipItem | null) => void>
    ): Promise<void> {
        // Group by both lang and langOverlay
        const requestedTooltipsGrouped = ldGroupBy(
            requestedTooltips,
            x => `${x.lang}|${x.langOverlay ?? ""}`
        );

        for (const groupKey in requestedTooltipsGrouped) {
            if (Object.prototype.hasOwnProperty.call(requestedTooltipsGrouped, groupKey)) {
                const tooltipsGroup = requestedTooltipsGrouped[groupKey];
                const requestedTooltipKeys = tooltipsGroup.map(x => x.key);

                // Split the composite key back to get lang and langOverlay
                const [lang, langOverlay] = groupKey.split("|");

                const tooltipsData = await firstValueFrom(
                    this._gateway.getValues(requestedTooltipKeys, lang, langOverlay || undefined)
                );

                if (tooltipsData.tooltips?.length > 0) {
                    for (const tooltip of tooltipsData.tooltips) {
                        responseHandlers[tooltip.id](tooltip);
                    }

                    // Resolve the handlers for which no tooltip data has been received (in order to keep Promise.all calls loading multiple tooltips working)
                    const loadedTooltipIds = tooltipsData.tooltips.map(x => x.id);
                    const missingTooltipIds = Object.keys(responseHandlers).filter(
                        x => !loadedTooltipIds.includes(x)
                    );
                    for (const x of missingTooltipIds) {
                        responseHandlers[x](null);
                    }
                }
            }
        }
    }
}
